import { cur } from '@/lib/currency';
import { getDeviceId } from '@/lib/deviceHeartbeat';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useWebSocket } from '@/hooks/useWebSocket';
import {
  Search, Plus, Minus, Trash2, ShoppingCart, CreditCard, Banknote, Users,
  ChefHat, X, Check, UtensilsCrossed, Grid, Star, LogOut, Coffee, Cake,
  Pizza, Salad, Armchair, ArrowLeft, RefreshCw, CircleDot, Clock, DollarSign,
  Info, Bell, Pencil, Map, List, Settings, AlertTriangle,
} from 'lucide-react';
import api from '@/lib/api';
import { resolveBranchModifiers } from '@/lib/menuModifiers';
import { isItemScheduleActive } from '@/lib/itemSchedule';
import PosItemsGrid from '@/components/cashier-pos/PosItemsGrid';
import { WaiterFloorView } from '@/components/tables/WaiterFloorView';
import { PosLoginBrand } from '@/components/pos/PosLoginBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORY_ICONS = {
  all: Grid, popular: Star, main: UtensilsCrossed, appetizer: Salad,
  beverage: Coffee, dessert: Cake, sides: Pizza,
};

const STATUS_COLORS = {
  available: { bg: 'bg-success/10 border-emerald-400 hover:bg-success/20', text: 'text-success', dot: 'bg-success', label: 'Available' },
  occupied: { bg: 'bg-error/10 border-red-400 hover:bg-error/20', text: 'text-error', dot: 'bg-error', label: 'Occupied' },
  payment_requested: { bg: 'bg-amber-100 border-amber-500 hover:bg-amber-200 ring-2 ring-amber-300', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Payment Requested' },
  reserved: { bg: 'bg-warning/10 border-amber-400 hover:bg-warning/20', text: 'text-warning', dot: 'bg-warning', label: 'Reserved' },
  cleaning: { bg: 'bg-info/10 border-blue-400 hover:bg-info/20', text: 'text-info', dot: 'bg-info', label: 'Cleaning' },
};

// Canonical kitchen state → Waiter display. Reuses the order's existing
// kitchen_status (set by the KDS via PUT /cashier/orders/{id}/status) — no
// second status field. Fired lines reflect the live kitchen stage instead of a
// static "Sent".
const KITCHEN_STATE = {
  preparing: { label: 'Preparing', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  ready:     { label: 'Ready',     cls: 'bg-blue-100 text-blue-700 border-blue-200' },
  served:    { label: 'Served',    cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  auto_closed: { label: 'Auto-closed by system', cls: 'bg-slate-100 text-slate-600 border-slate-200' },
};

export default function WaiterPage() {
  const navigate = useNavigate();
  const [waiter, setWaiter] = useState(null);
  const [pin, setPin] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [setupReason, setSetupReason] = useState('');

  // Views: 'login' | 'tables' | 'order'
  const [view, setView] = useState('login');

  // Table data
  const [sections, setSections] = useState([]);
  const [tables, setTables] = useState([]);
  const [activeSection, setActiveSection] = useState('all');
  const [selectedTable, setSelectedTable] = useState(null);
  // D3 — optional spatial floor view (read-only). List stays default.
  const [floorMode, setFloorMode] = useState(false);

  // Order data
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0.15); // branch VAT (same source as Cashier POS)
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const sendingRef = useRef(false);
  const [sending, setSending] = useState(false);
  // Multi-check: all open checks on the selected table (A/B/C…)
  const [openChecks, setOpenChecks] = useState([]);

  // Dialogs
  const [showModifiers, setShowModifiers] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [showPayment, setShowPayment] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [showCustomerSelect, setShowCustomerSelect] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showMobileCart, setShowMobileCart] = useState(false);
  // Phase B — optional check naming (reuses check_label).
  const [showRename, setShowRename] = useState(false);
  const [renameValue, setRenameValue] = useState('');

  // Check stored auth
  useEffect(() => {
    const waiterData = localStorage.getItem('waiter_user');
    const waiterToken = localStorage.getItem('waiter_token');
    if (waiterData && waiterToken) {
      setWaiter(JSON.parse(waiterData));
      setView('tables');
    }
    let tb = '';
    try { tb = localStorage.getItem('pos_terminal_branch_id') || ''; } catch { /* ignore */ }
    setSetupReason(tb ? '' : 'missing');
  }, []);

  // Fetch tables
  const fetchTables = useCallback(async () => {
    const token = localStorage.getItem('waiter_token');
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let branchId = '';
      try { branchId = JSON.parse(localStorage.getItem('waiter_user') || '{}')?.branch_id || ''; } catch { /* ignore */ }
      const tabRes = await api.get('/tables', { headers, params: branchId ? { branch_id: branchId, fallback_unassigned: true } : {} });
      const legacy = !branchId || (tabRes.data.length > 0 && !tabRes.data[0].branch_id);
      const secRes = await api.get('/tables/sections', { headers, params: branchId ? { branch_id: legacy ? '__unassigned__' : branchId } : {} });
      setSections(secRes.data);
      setTables(tabRes.data);
    } catch (err) {
      console.error('Failed to fetch tables:', err);
    }
  }, []);

  // Fetch menu
  const fetchMenu = useCallback(async () => {
    const token = localStorage.getItem('waiter_token');
    if (!token) return;
    try {
      const headers = { Authorization: `Bearer ${token}` };
      // iter905 — Pass branch_id when fetching categories + menu so a
      // waiter on Branch A never sees Branch B's items. Reads from the
      // stashed waiter_user in localStorage (populated on login).
      let _cachedBranch = null;
      try {
        const _raw = localStorage.getItem('waiter_user');
        _cachedBranch = _raw ? (JSON.parse(_raw)?.branch_id || null) : null;
      } catch { /* ignore */ }
      const menuParams = _cachedBranch ? { branch_id: _cachedBranch } : {};
      const [catRes, menuRes, custRes, taxRes] = await Promise.all([
        api.get('/cashier/categories', { headers, params: menuParams }),
        api.get('/cashier/menu', { headers, params: menuParams }),
        api.get('/cashier/customers', { headers }),
        api.get('/cashier/branch-tax-info', { headers, params: menuParams }).catch(() => ({ data: null })),
      ]);
      if (taxRes.data && taxRes.data.tax_rate != null) setTaxRate(Number(taxRes.data.tax_rate) / 100);
      setCategories(catRes.data);
      setMenuItems(menuRes.data);
      setCustomers(custRes.data);
    } catch (err) {
      console.error('Failed to fetch menu:', err);
    }
  }, []);

  useEffect(() => {
    if (view === 'tables') fetchTables();
    if (view === 'order') fetchMenu();
  }, [view, fetchTables, fetchMenu]);

  // Realtime — reuse the existing ws:dashboard bus. Cashier/kitchen/other-waiter
  // check changes refresh the tables immediately. When the waiter is viewing an
  // open check, pull the live kitchen_status for THAT check so the screen
  // reflects Preparing → Ready → Served without a manual refresh. A slow 30s
  // poll stays as a safety-net fallback (no constant polling).
  const currentOrderIdRef = useRef(null);
  useEffect(() => { currentOrderIdRef.current = currentOrder?.id || null; }, [currentOrder?.id]);
  const handleWaiterWs = useCallback((msg) => {
    const type = msg?.type || msg?.event;
    if (!['check_opened', 'check_items_added', 'check_payment_requested', 'check_completed', 'new_sale'].includes(type)) return;
    if (view === 'tables') { fetchTables(); return; }
    if (view === 'order' && selectedTable) {
      (async () => {
        const checks = await loadChecks(selectedTable.id);
        const fresh = checks.find((c) => c.id === currentOrderIdRef.current);
        if (fresh) setCurrentOrder(fresh);
      })();
    }
  }, [view, fetchTables, selectedTable]);
  useWebSocket(handleWaiterWs);

  // Fallback refresh (realtime above handles the common case).
  useEffect(() => {
    if (view !== 'tables') return;
    const interval = setInterval(fetchTables, 30000);
    return () => clearInterval(interval);
  }, [view, fetchTables]);

  // PIN Login
  const handleLogin = async () => {
    if (pin.length < 4) { toast.error('Enter your 4-digit PIN'); return; }
    // F1 — Reuse the SAME terminal-branch mechanism as Cashier login
    // (localStorage `pos_terminal_branch_id`, pinned via Settings →
    // Printers → Setup → "This device's branch"). The waiter session's
    // active branch becomes this pinned branch. Without it the backend
    // (correctly) rejects PIN login with "Branch required".
    let terminalBranchId = '';
    try { terminalBranchId = localStorage.getItem('pos_terminal_branch_id') || ''; } catch { /* ignore */ }
    if (!terminalBranchId) {
      setSetupReason('missing');
      return;
    }
    setLoginLoading(true);
    try {
      const res = await fetch(`${API_URL}/cashier/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, device_id: getDeviceId(), branch_id: terminalBranchId }),
      });
      if (!res.ok) {
        if (res.status === 404) { setSetupReason('invalid'); setPin(''); setLoginLoading(false); return; }
        const err = await res.json();
        throw new Error(err.detail || 'Invalid PIN');
      }
      const data = await res.json();
      const posRole = data.user?.pos_role || 'both';
      
      // Check if user has waiter access
      if (posRole === 'cashier') {
        toast.error('This PIN is for cashier access only. Please use Cashier POS.');
        setPin('');
        setLoginLoading(false);
        return;
      }
      
      localStorage.setItem('waiter_token', data.access_token);
      if (data.device_assigned_branch_id) localStorage.setItem('pos_terminal_branch_id', data.device_assigned_branch_id);
      localStorage.setItem('waiter_user', JSON.stringify(data.user));
      setWaiter(data.user);
      setView('tables');
      toast.success(`Welcome, ${data.user.name}!`);
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('waiter_token');
    localStorage.removeItem('waiter_user');
    setWaiter(null);
    setView('login');
    setPin('');
  };

  // Select table and start/resume order
  const handleSelectTable = async (table) => {
    const token = localStorage.getItem('waiter_token');
    const headers = { Authorization: `Bearer ${token}` };

    if (table.status === 'occupied' && table.current_order_id) {
      // Resume existing order
      try {
        const res = await api.post(`/tables/${table.id}/start-order`, { waiter_id: waiter?.id, branch_id: waiter?.branch_id }, { headers });
        setSelectedTable(table);
        setCurrentOrder(res.data.order);
        setCart(mapOrderItemsToCart(res.data.order?.items || []));
        setView('order');
        loadChecks(table.id);
      } catch (err) {
        toast.error('Failed to load order');
      }
    } else if (table.status === 'available') {
      // Start new order
      try {
        const res = await api.post(`/tables/${table.id}/start-order`, {
          waiter_id: waiter?.id,
          branch_id: waiter?.branch_id,
          customer_count: 1,
        }, { headers });
        setSelectedTable(table);
        setCurrentOrder(res.data.order);
        setCart([]);
        setView('order');
        loadChecks(table.id);
        toast.success(`Order started for table ${table.table_number}`);
      } catch (err) {
        toast.error('Failed to start order');
      }
    } else if (table.status === 'cleaning') {
      // Mark as available
      try {
        await api.post(`/tables/${table.id}/mark-available`, {}, { headers });
        toast.success('Table marked as available');
        fetchTables();
      } catch (err) {
        toast.error('Failed to update table');
      }
    } else {
      toast.info(`Table ${table.table_number} is ${table.status}`);
    }
  };

  const mapOrderItemsToCart = (items) => {
    return items.map(item => ({
      item_id: item.item_id || item.id,
      name: item.name,
      name_ar: item.name_ar,
      category: item.category,
      unit_price: item.price || item.unit_price || 0,
      modifiers: item.modifiers || [],
      modifier_total: (item.modifiers || []).reduce((s, m) => s + (m.price || 0), 0),
      quantity: item.quantity || 1,
      subtotal: (item.price || item.unit_price || 0) * (item.quantity || 1),
      existing: true,
    }));
  };

  // Multi-check: load every open check on a table so the waiter can switch
  // between them. Returns the list (also stored in state).
  const loadChecks = async (tableId) => {
    const token = localStorage.getItem('waiter_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await api.get(`/tables/${tableId}/checks`, { headers, params: waiter?.branch_id ? { branch_id: waiter.branch_id } : {} });
      const checks = res.data?.checks || [];
      setOpenChecks(checks);
      return checks;
    } catch {
      setOpenChecks([]);
      return [];
    }
  };

  // Switch the active check — loads THAT check's items into the cart. Every
  // subsequent waiter action (add items / request payment) targets its id.
  const switchCheck = (order) => {
    if (!order) return;
    setCurrentOrder(order);
    setCart(mapOrderItemsToCart(order.items || []));
    toast.success(`Switched to ${order.check_label || 'check'}`);
  };

  // Menu filtering
  const filteredItems = menuItems.filter(item => {
    if (selectedCategory !== 'all' && selectedCategory !== 'popular') {
      if (item.category !== selectedCategory) return false;
    }
    if (selectedCategory === 'popular' && !item.tags?.includes('popular')) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return item.name.toLowerCase().includes(q) || item.name_ar?.includes(q);
    }
    return true;
  });

  // Cart calculations
  const newItems = cart.filter(c => !c.existing);
  // Live kitchen stage for the open check (canonical order.kitchen_status).
  const kitchenState = currentOrder?.kitchen_status ? KITCHEN_STATE[currentOrder.kitchen_status] : null;
  // Same basis as checkout: VAT-inclusive menu items carry VAT inside their price.
  const inclusiveIds = new Set(menuItems.filter((m) => m.price_includes_tax).map((m) => m.id));
  const subtotal = cart.reduce((sum, item) => sum + (inclusiveIds.has(item.item_id) ? item.subtotal / (1 + taxRate) : item.subtotal), 0);
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  // Add item — branch-aware sizes/add-ons + per-size branch price via the
  // SAME shared helper the Cashier POS uses, so Waiter shows exactly the same
  // enabled sizes and branch-specific prices. Pricing semantics unchanged.
  const handleAddItem = (item) => {
    const branchId = waiter?.branch_id || null;
    const groups = resolveBranchModifiers(item, branchId);
    if (groups.length > 0) {
      setSelectedItem({ ...item, _filteredModifiers: groups });
      setSelectedModifiers({});
      setShowModifiers(true);
    } else {
      addToCart(item, []);
    }
  };

  const addToCart = (item, modifiers) => {
    const modifierTotal = modifiers.reduce((sum, m) => sum + (m.price || 0), 0);
    const itemTotal = item.price + modifierTotal;
    const existingIndex = cart.findIndex(c =>
      c.item_id === item.id && !c.existing &&
      JSON.stringify(c.modifiers) === JSON.stringify(modifiers)
    );
    if (existingIndex >= 0) {
      const updated = [...cart];
      updated[existingIndex].quantity += 1;
      updated[existingIndex].subtotal = updated[existingIndex].quantity * itemTotal;
      setCart(updated);
    } else {
      setCart([...cart, {
        item_id: item.id, name: item.name, name_ar: item.name_ar,
        category: item.category,
        unit_price: item.price, modifiers, modifier_total: modifierTotal,
        quantity: 1, subtotal: itemTotal, existing: false,
      }]);
    }
    toast.success(`${item.name} added`);
  };

  const confirmModifiers = () => {
    const modifiers = [];
    for (const [groupName, selection] of Object.entries(selectedModifiers)) {
      if (Array.isArray(selection)) {
        selection.forEach(s => modifiers.push({ group: groupName, ...s }));
      } else if (selection) {
        modifiers.push({ group: groupName, ...selection });
      }
    }
    addToCart(selectedItem, modifiers);
    setShowModifiers(false);
  };

  const updateQuantity = (index, delta) => {
    const updated = [...cart];
    updated[index].quantity += delta;
    if (updated[index].quantity <= 0) {
      updated.splice(index, 1);
    } else {
      const itemTotal = updated[index].unit_price + updated[index].modifier_total;
      updated[index].subtotal = updated[index].quantity * itemTotal;
    }
    setCart(updated);
  };

  // Send new items to kitchen
  const handleSendToKitchen = async () => {
    if (newItems.length === 0) { toast.info('No new items to send'); return; }
    if (sendingRef.current) return;
    sendingRef.current = true; setSending(true);
    const clientRequestId = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const token = localStorage.getItem('waiter_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const items = newItems.map(c => ({
        item_id: c.item_id, name: c.name, name_ar: c.name_ar,
        category: c.category,
        price: c.unit_price, quantity: c.quantity,
        modifiers: c.modifiers, notes: '',
      }));
      await api.post(`/tables/${selectedTable.id}/add-items`, { items, order_id: currentOrder?.id, client_request_id: clientRequestId, branch_id: waiter?.branch_id }, { headers });
      toast.success('Items sent to kitchen!');
      // Mark all items as existing
      setCart(cart.map(c => ({ ...c, existing: true })));
      // Refresh THIS check (not current_order_id) + the table's check list.
      const checks = await loadChecks(selectedTable.id);
      const fresh = checks.find(c => c.id === currentOrder?.id);
      if (fresh) setCurrentOrder(fresh);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send items');
    } finally { sendingRef.current = false; setSending(false); }
  };

  // Pre-bill "check slip" — NOT a tax invoice. Reuses the browser/WebView print
  // path (same rail the POS receipt uses on-device). Final ZATCA invoice is
  // issued by the cashier through the canonical POS completion.
  const printCheckSlip = () => {
    if (!currentOrder) return;
    const rows = (cart || []).map((c) =>
      `<tr><td>${c.name || c.item_id}</td><td style="text-align:center">${c.quantity}</td><td style="text-align:right">${(c.subtotal || 0).toFixed(2)}</td></tr>`).join('');
    const html = `<html><head><title>Check Slip</title><style>body{font-family:monospace;padding:8px;width:280px}h3{margin:4px 0}table{width:100%;border-collapse:collapse;font-size:12px}td{padding:2px 0}hr{border:none;border-top:1px dashed #000}</style></head><body>
      <h3 style="text-align:center">CHECK SLIP</h3>
      <div style="text-align:center;font-size:11px">Not a tax invoice</div><hr/>
      <div>Table: ${selectedTable?.table_number || ''} ${currentOrder.check_label || ''}</div>
      <div>Order: ${currentOrder.order_number || ''}</div>
      <div>Waiter: ${waiter?.name || ''}</div>
      <div>${new Date().toLocaleString()}</div><hr/>
      <table><tr><td><b>Item</b></td><td style="text-align:center"><b>Qty</b></td><td style="text-align:right"><b>Amt</b></td></tr>${rows}</table><hr/>
      <div style="text-align:right;font-weight:bold">TOTAL: ${cur()} ${total.toFixed(2)}</div>
      <div style="text-align:center;font-size:11px;margin-top:8px">Please pay at the counter</div>
    </body></html>`;
    const w = window.open('', '_blank', 'width=320,height=600');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  };

  // Request Payment — the backend /close-order no longer finalizes money; it
  // flags the check as payment_requested and alerts the branch cashier via the
  // existing realtime bus. Actual completion happens at the POS (canonical).
  const handleCloseOrder = async () => {
    if (!selectedTable) return;
    const token = localStorage.getItem('waiter_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await api.post(`/tables/${selectedTable.id}/close-order`, {
        order_id: currentOrder?.id,
        branch_id: waiter?.branch_id,
        payment_mode: paymentMethod,
        amount_paid: total,
      }, { headers });
      toast.success('Payment requested — sent to the cashier counter');
      setShowPayment(false);
      setCart([]);
      setCurrentOrder(null);
      setOpenChecks([]);
      setSelectedTable(null);
      setView('tables');
      fetchTables();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to request payment');
    }
  };

  // Multi-check — start another independent check on the SAME table.
  const handleNewCheck = async () => {
    if (!selectedTable) return;
    const token = localStorage.getItem('waiter_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const res = await api.post(`/tables/${selectedTable.id}/start-order`,
        { waiter_id: waiter?.id, branch_id: waiter?.branch_id, new_check: true, customer_count: 1 }, { headers });
      setCurrentOrder(res.data.order);
      setCart([]);
      await loadChecks(selectedTable.id);
      toast.success(`Started ${res.data.order?.check_label || 'new check'} on table ${selectedTable.table_number}`);
    } catch (err) {
      toast.error('Failed to start new check');
    }
  };

  // Phase B — preserve the automatic "Check A/B/C" base; the optional
  // customer/group name is appended as "Check A • Family 1".
  const baseLabel = (label) => (label || 'Check').split(' • ')[0];
  const openRename = () => {
    const cur = currentOrder?.check_label || '';
    setRenameValue(cur.includes(' • ') ? cur.split(' • ').slice(1).join(' • ') : '');
    setShowRename(true);
  };
  const handleRenameCheck = async () => {
    if (!currentOrder || !selectedTable) return;
    const token = localStorage.getItem('waiter_token');
    const headers = { Authorization: `Bearer ${token}` };
    const base = baseLabel(currentOrder.check_label);
    const name = renameValue.trim();
    const newLabel = name ? `${base} • ${name}` : base;
    try {
      await api.put(`/cashier/orders/${currentOrder.id}`, { check_label: newLabel }, { headers });
      setShowRename(false);
      const checks = await loadChecks(selectedTable.id);
      const fresh = checks.find((c) => c.id === currentOrder.id);
      if (fresh) setCurrentOrder(fresh);
      toast.success(name ? `Named "${newLabel}"` : 'Check name cleared');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to rename check');
    }
  };

  const goBackToTables = () => {
    setView('tables');
    setSelectedTable(null);
    setCurrentOrder(null);
    setOpenChecks([]);
    setCart([]);
    setSearchQuery('');
    setSelectedCategory('all');
    fetchTables();
  };

  // ============ LOGIN VIEW ============
  if (view === 'login') {
    if (setupReason) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-stone-100 to-orange-50 flex items-center justify-center p-4" data-testid="waiter-terminal-setup">
          <Card className="w-full max-w-sm shadow-2xl border-0">
            <CardContent className="p-8 text-center space-y-4">
              <PosLoginBrand role="Waiter" icon={Armchair} />
              <div className="mx-auto w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="text-amber-600" size={28} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-foreground">This Terminal Is Not Set Up</h2>
                <p className="text-sm text-muted-foreground mt-1" data-testid="waiter-setup-message">
                  {setupReason === 'invalid'
                    ? 'Terminal branch configuration is no longer valid — Admin Setup required.'
                    : 'A branch must be assigned to this device before Waiter can be used.'}
                </p>
              </div>
              <Button onClick={() => navigate('/settings/printers?terminalSetup=1&returnTo=/waiter')} className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-semibold gap-2" data-testid="waiter-admin-setup-btn">
                <Settings size={18} /> Admin Setup
              </Button>
              <a href="/login" className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1 justify-center">
                <ArrowLeft size={12} /> Main Login
              </a>
            </CardContent>
          </Card>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-100 to-orange-50 flex items-center justify-center p-4" data-testid="waiter-login">
        <Card className="w-full max-w-sm shadow-2xl border-0">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <PosLoginBrand role="Waiter" icon={Armchair} />
              <p className="text-muted-foreground text-sm mt-1">Enter your PIN to start</p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'del'].map((key, i) => (
                  <Button
                    key={i}
                    variant="outline"
                    className={`h-14 text-xl font-bold rounded-xl ${key === null ? 'invisible' : ''} ${typeof key === 'number' ? 'hover:bg-orange-50' : ''}`}
                    onClick={() => {
                      if (key === 'del') setPin(p => p.slice(0, -1));
                      else if (typeof key === 'number' && pin.length < 4) setPin(p => p + key);
                    }}
                    data-testid={`pin-key-${key}`}
                  >
                    {key === 'del' ? <X size={20} /> : key}
                  </Button>
                ))}
              </div>
              <div className="flex justify-center gap-3 my-4">
                {[0, 1, 2, 3].map(i => (
                  <div
                    key={i}
                    className={`w-4 h-4 rounded-full transition-all ${i < pin.length ? 'bg-orange-500 scale-110' : 'bg-muted'}`}
                  />
                ))}
              </div>
              <Button
                className="w-full h-12 text-lg bg-orange-500 hover:bg-orange-600"
                onClick={handleLogin}
                disabled={pin.length < 4 || loginLoading}
                data-testid="waiter-login-btn"
              >
                {loginLoading ? 'Signing in...' : 'Sign In'}
              </Button>
              <Button variant="ghost" className="w-full" onClick={() => navigate('/cashier')}>
                Switch to Cashier
              </Button>
              <Button variant="ghost" className="w-full text-xs text-muted-foreground" onClick={() => navigate('/kds')}>
                Kitchen Display
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============ TABLES VIEW ============
  if (view === 'tables') {
    const filteredTables = activeSection === 'all' ? tables : tables.filter(t => t.section === activeSection);
    // D3 — a section is "manager-configured" when ANY of its tables has a saved
    // non-zero coordinate (frontend auto-arrange is never persisted, so a
    // non-zero coord can only come from an intentional D2 Save Layout). Floor
    // View is only offered for a concrete, configured section; otherwise the D1
    // grouped grid remains the default/fallback.
    const concreteSection = activeSection !== 'all' ? activeSection : null;
    const sectionConfigured = !!concreteSection && tables.some(
      t => t.section === concreteSection && (t.position_x > 0 || t.position_y > 0)
    );
    const showFloor = floorMode && sectionConfigured;
    return (
      <div className="min-h-screen bg-muted/50" data-testid="waiter-tables">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Armchair size={20} className="text-orange-600" />
            </div>
            <div>
              <h1 className="font-bold text-lg font-outfit">Select Table</h1>
              <p className="text-xs text-muted-foreground">{waiter?.name} &middot; Waiter Mode</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* iter700 — How-this-connects tooltip for waiters so they
                understand the flow without needing an admin to explain. */}
            <details className="relative">
              <summary className="list-none cursor-pointer inline-flex items-center justify-center w-9 h-9 rounded-lg bg-orange-50 hover:bg-orange-100 text-orange-600" data-testid="waiter-help-trigger" title="How this connects to the cashier">
                <Info size={16} />
              </summary>
              <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-orange-200 rounded-xl shadow-xl p-3 z-30 text-xs leading-relaxed text-zinc-700 space-y-1.5" data-testid="waiter-help-content">
                <p className="font-semibold text-zinc-900">Waiter → Kitchen → Cashier flow</p>
                <ol className="list-decimal ml-4 space-y-1">
                  <li>Pick a table → it goes <strong className="text-rose-600">red (occupied)</strong> for everyone on the same branch.</li>
                  <li>Add items → they print on the kitchen ticket within seconds and show on the <strong>Kitchen Display</strong>.</li>
                  <li>When the chef marks <em>Ready</em>, you see it here AND on the cashier's <strong>Orders</strong> dialog.</li>
                  <li>Close the order with the chosen payment method (cash / card / bank) — the sale lands in <strong>Day Report</strong> tagged to this branch automatically.</li>
                </ol>
                <p className="pt-1 border-t border-orange-100 text-[10px] text-zinc-500">All tablets at the same branch (waiter, cashier, kitchen) share live data — no manual refresh needed.</p>
              </div>
            </details>
            <Button size="sm" variant="ghost" onClick={fetchTables} data-testid="refresh-waiter-tables">
              <RefreshCw size={16} />
            </Button>
            <Button size="sm" variant="outline" onClick={handleLogout} data-testid="waiter-logout-btn">
              <LogOut size={16} className="mr-1" /> Logout
            </Button>
          </div>
        </div>

        {/* Section tabs */}
        <div className="bg-white border-b px-4 py-2 overflow-x-auto">
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setActiveSection('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                activeSection === 'all' ? 'bg-orange-500 text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              All
            </button>
            {sections.map(sec => (
              <button
                key={sec.id}
                onClick={() => setActiveSection(sec.name)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap`}
                style={{
                  backgroundColor: activeSection === sec.name ? sec.color : `${sec.color}20`,
                  color: activeSection === sec.name ? 'white' : sec.color,
                }}
              >
                {sec.name}
              </button>
            ))}
            {/* D3 — List ⇄ Floor toggle, only for a configured concrete section */}
            {sectionConfigured && (
              <div className="ml-auto flex rounded-lg border border-border overflow-hidden shrink-0" data-testid="waiter-view-toggle">
                <button
                  onClick={() => setFloorMode(false)}
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${!floorMode ? 'bg-orange-500 text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                  data-testid="waiter-view-list"
                >
                  <List size={14} /> List
                </button>
                <button
                  onClick={() => setFloorMode(true)}
                  className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${floorMode ? 'bg-orange-500 text-white' : 'bg-transparent text-muted-foreground hover:bg-muted'}`}
                  data-testid="waiter-view-floor"
                >
                  <Map size={14} /> Floor View
                </button>
              </div>
            )}
          </div>
        </div>

        {showFloor ? (
          <WaiterFloorView
            tables={filteredTables}
            sectionName={concreteSection}
            onSelectTable={handleSelectTable}
          />
        ) : (
        /* Tables Grid — grouped by Section/Floor (Phase D1 visual floor) */
        <div className="p-4 space-y-6">
          {(() => {
            const groups = {};
            filteredTables.forEach((t) => {
              const key = t.section || 'Other';
              (groups[key] = groups[key] || []).push(t);
            });
            const order = sections.map((s) => s.name).filter((n) => groups[n]);
            Object.keys(groups).forEach((k) => { if (!order.includes(k)) order.push(k); });
            return order.map((secName) => {
              const sec = sections.find((s) => s.name === secName);
              const secTables = groups[secName];
              return (
                <div key={secName} data-testid={`floor-section-${secName}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: sec?.color || '#f97316' }} />
                    <h2 className="font-semibold text-base font-outfit">{secName}</h2>
                    {sec?.floor != null && <span className="text-xs text-muted-foreground">· Floor {sec.floor}</span>}
                    <span className="text-xs text-muted-foreground">· {secTables.length} tables</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                    {secTables.map((table) => {
                      const displayStatus = table.payment_requested ? 'payment_requested' : table.status;
                      const s = STATUS_COLORS[displayStatus] || STATUS_COLORS.available;
                      const checksCount = table.open_checks_count || 0;
                      return (
                        <button
                          key={table.id}
                          onClick={() => handleSelectTable(table)}
                          className={`relative border-2 rounded-2xl p-5 transition-all ${s.bg} text-left`}
                          data-testid={`waiter-table-${table.table_number}`}
                        >
                          {checksCount > 1 && (
                            <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-full bg-zinc-900 text-white text-[10px] font-bold" data-testid={`table-checks-count-${table.table_number}`}>
                              {checksCount} Checks
                            </span>
                          )}
                          <div className="text-center">
                            <span className="text-2xl font-bold font-outfit block">{table.table_number}</span>
                            <div className="flex items-center justify-center gap-1 mt-2">
                              <Users size={14} className={s.text} />
                              <span className={`text-sm font-medium ${s.text}`}>{table.customer_count || 0}/{table.capacity}</span>
                            </div>
                            <Badge variant="outline" className={`mt-2 text-xs ${s.text} border-current`} data-testid={`table-status-${table.table_number}`}>
                              {table.payment_requested ? <Bell size={8} className="mr-1" /> : <CircleDot size={8} className="mr-1" />}
                              {s.label}
                            </Badge>
                            {table.current_order && (
                              <p className="mt-2 text-sm font-bold text-error">{cur()} {table.current_order.total?.toFixed(2)}</p>
                            )}
                            {table.status === 'occupied' && (
                              <p className="text-xs text-muted-foreground mt-1">Tap to manage</p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
          {filteredTables.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <Armchair size={48} className="mx-auto mb-3 opacity-30" />
              <p>No tables found</p>
            </div>
          )}
        </div>
        )}

        {/* Legend */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t px-4 py-3 flex justify-center flex-wrap gap-x-6 gap-y-1 text-xs">
          {Object.entries(STATUS_COLORS).map(([status, colors]) => (
            <div key={status} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} />
              <span>{colors.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ============ ORDER VIEW ============

  return (
    <div className="h-screen flex flex-col md:flex-row bg-muted overflow-hidden" data-testid="waiter-order">
      {/* Left: Menu Section */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white border-b p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <Button size="sm" variant="ghost" onClick={goBackToTables} data-testid="back-to-tables">
              <ArrowLeft size={18} />
            </Button>
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <Armchair size={16} className="text-orange-600 sm:hidden" />
              <Armchair size={20} className="text-orange-600 hidden sm:block" />
            </div>
            <div>
              <h1 className="font-bold text-base sm:text-lg font-outfit flex items-center gap-1.5" data-testid="order-table-title">
                <span>Table {selectedTable?.table_number}</span>
                {currentOrder?.check_label && (
                  <span className="text-orange-600" data-testid="current-check-label">· {currentOrder.check_label}</span>
                )}
                {currentOrder && (
                  <button
                    onClick={openRename}
                    className="p-1 rounded-md text-orange-500 hover:bg-orange-50"
                    title="Name / rename this check"
                    data-testid="rename-check-btn"
                  >
                    <Pencil size={14} />
                  </button>
                )}
              </h1>
              <p className="text-[10px] sm:text-xs text-muted-foreground">{waiter?.name} &middot; {currentOrder?.order_number || 'New Order'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="text-xs" onClick={handleNewCheck} data-testid="new-check-btn">
              + New Check
            </Button>
            <Badge variant="outline" className="text-xs sm:text-sm px-2 sm:px-3 py-1 hidden sm:flex">
              <Clock size={14} className="mr-1" />
              {currentOrder?.created_at ? new Date(currentOrder.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
            </Badge>
            {/* Mobile cart toggle */}
            <Button size="sm" className="md:hidden bg-orange-500 hover:bg-orange-600 relative" onClick={() => setShowMobileCart(true)} data-testid="mobile-cart-toggle">
              <ShoppingCart size={16} />
              {cart.length > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 bg-error rounded-full text-[10px] flex items-center justify-center text-white">{cart.length}</span>}
            </Button>
          </div>
        </div>

        {/* Multi-check switcher — select which check to work on */}
        {openChecks.length > 1 && (
          <div className="px-3 py-2 bg-orange-50 border-b overflow-x-auto" data-testid="check-switcher">
            <div className="flex gap-2 items-center">
              <span className="text-[11px] text-orange-700 font-semibold shrink-0">Checks:</span>
              {openChecks.map((c) => (
                <button
                  key={c.id}
                  onClick={() => switchCheck(c)}
                  data-testid={`check-tab-${c.id}`}
                  className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    currentOrder?.id === c.id
                      ? 'bg-orange-500 text-white border-orange-500'
                      : 'bg-white text-orange-700 border-orange-200 hover:bg-orange-100'
                  }`}
                >
                  {c.check_label || 'Check'}
                  <span className="ml-1 opacity-80">· {Number(c.total || 0).toFixed(2)}</span>
                  {c.payment_requested && <Bell className="w-3 h-3 ml-1 inline" />}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-3 bg-white border-b">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-3 text-muted-foreground" />
            <Input
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11 rounded-xl"
              data-testid="waiter-menu-search"
            />
          </div>
        </div>

        {/* Categories */}
        <div className="p-3 bg-white border-b overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex gap-2 flex-nowrap">
            {categories.map(cat => {
              const Icon = CATEGORY_ICONS[cat.id] || Grid;
              return (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`shrink-0 flex flex-col items-center gap-1 px-4 py-2 rounded-xl transition-all min-w-[72px] ${
                    selectedCategory === cat.id
                      ? 'bg-orange-500 text-white'
                      : 'bg-muted text-muted-foreground hover:bg-muted'
                  }`}
                  data-testid={`waiter-cat-${cat.id}`}
                >
                  <Icon size={18} />
                  <span className="text-xs font-medium whitespace-nowrap">{cat.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Menu Grid — reuses the shared Cashier PosItemsGrid for identical
            card layout, sold-out/off-hours states, "Options" badge, popular
            star and branch pricing. Waiter's add-to-check engine is unchanged. */}
        <PosItemsGrid
          filteredItems={filteredItems}
          categories={categories}
          selectedCategory={selectedCategory}
          searchQuery={searchQuery}
          currencySymbol={cur()}
          isComfortable={true}
          isItemScheduleActive={isItemScheduleActive}
          onAddItem={handleAddItem}
          taxRate={0}
        />
      </div>

      {/* Right: Cart / Order Panel - Hidden on mobile, shown as overlay */}
      <div className={`${showMobileCart ? 'fixed inset-0 z-50 bg-black/50 md:relative md:inset-auto md:bg-transparent' : 'hidden md:flex'} md:w-80 lg:w-96`}>
        <div className={`${showMobileCart ? 'absolute right-0 top-0 bottom-0 w-[85vw] max-w-96 animate-in slide-in-from-right' : 'w-full'} bg-white border-l flex flex-col h-full`}>
        {/* Cart Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShoppingCart size={18} className="text-orange-600" />
              <h2 className="font-bold font-outfit text-sm">Table {selectedTable?.table_number} Order</h2>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-100 text-orange-700 text-xs">{cart.length} items</Badge>
              {kitchenState && (
                <Badge className={`text-[10px] border ${kitchenState.cls}`} data-testid="waiter-kitchen-status">
                  {kitchenState.label}
                </Badge>
              )}
              <Button size="sm" variant="ghost" className="md:hidden h-7 w-7 p-0" onClick={() => setShowMobileCart(false)} data-testid="close-mobile-cart">
                <X size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <ShoppingCart size={40} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm">Add items from the menu</p>
            </div>
          ) : (
            cart.map((item, index) => (
              <div
                key={index}
                className={`rounded-xl p-3 ${item.existing ? 'bg-muted/50 opacity-70' : 'bg-orange-50 border border-orange-200'}`}
                data-testid={`waiter-cart-item-${index}`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                      <h4 className="font-medium text-sm">{item.name}</h4>
                      {item.existing && (
                        <Badge variant="outline" className={`text-[9px] h-4 ${kitchenState ? kitchenState.cls : ''}`} data-testid={`waiter-item-kstatus-${index}`}>
                          {kitchenState ? kitchenState.label : 'Sent'}
                        </Badge>
                      )}
                    </div>
                    {item.modifiers?.length > 0 && (
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {item.modifiers.map((m, i) => <span key={i} className="mr-1">+{m.name}</span>)}
                      </div>
                    )}
                  </div>
                  {!item.existing && (
                    <button onClick={() => { const c = [...cart]; c.splice(index, 1); setCart(c); }} className="p-1">
                      <Trash2 size={14} className="text-error" />
                    </button>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    {!item.existing && (
                      <>
                        <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQuantity(index, -1)}>
                          <Minus size={12} />
                        </Button>
                        <span className="font-bold w-6 text-center text-sm">{item.quantity}</span>
                        <Button size="icon" variant="outline" className="h-6 w-6" onClick={() => updateQuantity(index, 1)}>
                          <Plus size={12} />
                        </Button>
                      </>
                    )}
                    {item.existing && <span className="text-sm text-muted-foreground">x{item.quantity}</span>}
                  </div>
                  <span className="font-bold text-sm">{cur()} {item.subtotal.toFixed(2)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Cart Footer */}
        <div className="border-t p-4 space-y-3">
          {/* Totals */}
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span>{cur()} {subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">VAT ({+(taxRate * 100).toFixed(2)}%)</span>
              <span>{cur()} {tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold pt-2 border-t">
              <span>Total</span>
              <span className="text-orange-600" data-testid="waiter-cart-total">{cur()} {total.toFixed(2)}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              className="h-12 bg-warning hover:bg-warning"
              onClick={handleSendToKitchen}
              disabled={newItems.length === 0 || sending}
              data-testid="send-to-kitchen-btn"
            >
              <ChefHat size={18} className="mr-1.5" />
              Kitchen ({newItems.length})
            </Button>
            <Button
              className="h-12 bg-success hover:bg-success"
              onClick={() => setShowPayment(true)}
              disabled={cart.length === 0}
              data-testid="close-order-btn"
            >
              <DollarSign size={18} className="mr-1.5" />
              Request Payment
            </Button>
          </div>
        </div>
      </div>
      </div>

      {/* Mobile floating cart button when cart is not visible */}
      {cart.length > 0 && !showMobileCart && (
        <div className="fixed bottom-4 left-4 right-4 md:hidden z-40" data-testid="mobile-cart-bar">
          <Button className="w-full h-14 bg-orange-500 hover:bg-orange-600 rounded-2xl shadow-lg" onClick={() => setShowMobileCart(true)}>
            <ShoppingCart size={20} className="mr-2" />
            <span className="font-bold">{cart.length} items</span>
            <span className="mx-2">|</span>
            <span className="font-bold">{cur()} {total.toFixed(2)}</span>
          </Button>
        </div>
      )}

      {/* Modifiers Dialog */}
      <Dialog open={showModifiers} onOpenChange={setShowModifiers}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedItem?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60dvh] overflow-y-auto">
            {(selectedItem?._filteredModifiers || selectedItem?.modifiers)?.map((group, gi) => (
              <div key={gi} className="space-y-2">
                <Label className="font-semibold">{group.name} {group.required && <span className="text-error">*</span>}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {group.options?.map((opt, oi) => {
                    const isSelected = group.multiple
                      ? selectedModifiers[group.name]?.some(s => s.name === opt.name)
                      : selectedModifiers[group.name]?.name === opt.name;
                    return (
                      <Button
                        key={oi}
                        variant={isSelected ? 'default' : 'outline'}
                        className={`justify-start h-auto py-2 ${isSelected ? 'bg-orange-500' : ''}`}
                        onClick={() => {
                          if (group.multiple) {
                            const current = selectedModifiers[group.name] || [];
                            const idx = current.findIndex(s => s.name === opt.name);
                            if (idx >= 0) current.splice(idx, 1);
                            else current.push(opt);
                            setSelectedModifiers({ ...selectedModifiers, [group.name]: [...current] });
                          } else {
                            setSelectedModifiers({ ...selectedModifiers, [group.name]: opt });
                          }
                        }}
                      >
                        <div className="text-left">
                          <p className="text-sm">{opt.name}</p>
                          {opt.price > 0 && <p className="text-xs opacity-70">+SR {opt.price}</p>}
                        </div>
                      </Button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModifiers(false)}>Cancel</Button>
            <Button onClick={confirmModifiers} className="bg-orange-500 hover:bg-orange-600">Add to Order</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPayment} onOpenChange={setShowPayment}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send to Cashier — Table {selectedTable?.table_number} {currentOrder?.check_label ? `· ${currentOrder.check_label}` : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-muted/50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span>Items</span>
                <span>{cart.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Subtotal</span>
                <span>{cur()} {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>VAT</span>
                <span>{cur()} {tax.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold border-t pt-2">
                <span>Total</span>
                <span className="text-orange-600">{cur()} {total.toFixed(2)}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              This alerts the cashier to collect payment at the counter. The final
              VAT invoice is issued by the cashier through the POS — this is not a tax invoice.
            </p>
            <Button variant="outline" className="w-full" onClick={printCheckSlip} data-testid="print-check-slip">
              <ChefHat size={16} className="mr-2" /> Print Check Slip (for customer)
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayment(false)}>Cancel</Button>
            <Button onClick={handleCloseOrder} className="bg-success hover:bg-success" data-testid="confirm-close-order">
              <Check size={18} className="mr-2" />
              Send to Cashier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Rename / Name Check Dialog (Phase B) */}
      <Dialog open={showRename} onOpenChange={setShowRename}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Name this check</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Optional. The automatic label{' '}
              <span className="font-semibold text-orange-600">{baseLabel(currentOrder?.check_label)}</span>{' '}
              is kept — your name is added after it, e.g. <span className="font-medium">{baseLabel(currentOrder?.check_label)} • Family 1</span>.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="check-name-input">Customer / group name</Label>
              <Input
                id="check-name-input"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                placeholder="e.g. Family 1, Mr. Ali…"
                maxLength={40}
                onKeyDown={(e) => { if (e.key === 'Enter') handleRenameCheck(); }}
                data-testid="check-name-input"
                autoFocus
              />
              <p className="text-[11px] text-muted-foreground">Leave empty to keep just {baseLabel(currentOrder?.check_label)}.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRename(false)} data-testid="rename-cancel-btn">Cancel</Button>
            <Button onClick={handleRenameCheck} className="bg-orange-500 hover:bg-orange-600" data-testid="rename-save-btn">
              <Check size={16} className="mr-1.5" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
